import { ref } from "vue";

const API_BASE = process.env.VUE_APP_API_URL || "";

function authHeaders() {
  try {
    const info = JSON.parse(localStorage.getItem("user-info") || "{}");
    const token = info.access_token || info.token || "";
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

const bookmarks = ref([]);
// One shared fetch per page load. Before this, bookmarks were fetched only on
// the Bookmarks page, so Tender Search showed bookmarked tenders with an empty
// star (and a click tried to add them again).
let loadPromise = null;

export function useBookmarks() {
  const isBookmarked = (id) => bookmarks.value.some((b) => b.tender_id === id);

  async function load() {
    try {
      const res = await fetch(`${API_BASE}/bookmarks`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      bookmarks.value = data.bookmarks || [];
    } catch {
      // silent — user stays on stale/empty state
    }
  }

  async function add(tender) {
    if (isBookmarked(tender.id)) return;
    // Optimistic update
    bookmarks.value.push({
      tender_id: tender.id,
      title: tender.title,
      tender_reference: tender.tender_reference,
      organisation_name: tender.organisation_name,
      closing_date: tender.closing_date,
      portal_code: tender.portal_code,
      bookmarked_at: new Date().toISOString(),
    });
    try {
      const res = await fetch(`${API_BASE}/bookmarks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ tender_id: tender.id }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      // patch in the server-assigned id
      const local = bookmarks.value.find((b) => b.tender_id === tender.id);
      if (local) local.id = data.id;
    } catch {
      // rollback
      bookmarks.value = bookmarks.value.filter((b) => b.tender_id !== tender.id);
      throw new Error("Failed to bookmark");
    }
  }

  async function remove(tenderId) {
    const snapshot = [...bookmarks.value];
    bookmarks.value = bookmarks.value.filter((b) => b.tender_id !== tenderId);
    try {
      const res = await fetch(`${API_BASE}/bookmarks/${tenderId}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error();
    } catch {
      bookmarks.value = snapshot;
      throw new Error("Failed to remove bookmark");
    }
  }

  async function toggle(tender) {
    if (isBookmarked(tender.id)) await remove(tender.id);
    else await add(tender);
  }

  async function clearAll() {
    const snapshot = [...bookmarks.value];
    bookmarks.value = [];
    try {
      const res = await fetch(`${API_BASE}/bookmarks/all`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error();
    } catch {
      bookmarks.value = snapshot;
      throw new Error("Failed to clear bookmarks");
    }
  }

  async function updateStatus(tenderId, status) {
    const bm = bookmarks.value.find((b) => b.tender_id === tenderId);
    if (!bm) return;
    const oldStatus = bm.project_status;
    bm.project_status = status;
    try {
      const res = await fetch(`${API_BASE}/bookmarks/${tenderId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ project_status: status }),
      });
      if (!res.ok) throw new Error();
    } catch {
      bm.project_status = oldStatus;
      throw new Error("Failed to update status");
    }
  }

  // Loads once and reuses the result; load() still forces a refresh.
  function ensureLoaded() {
    if (!loadPromise) loadPromise = load();
    return loadPromise;
  }

  return { bookmarks, isBookmarked, load, ensureLoaded, add, remove, toggle, clearAll, updateStatus };
}

